/**
 * Optionale Hardware-PTT-Anbindung (UNIWA BM008 & kompatible BLE-Taster).
 *
 * VOLLSTÄNDIG OPTIONAL: Dieses Modul tut nichts, solange niemand `hwConnect()`
 * aufruft. Die Intercom funktioniert unverändert ohne Hardware.
 *
 * Zwei Quellen liefern denselben Event-Strom:
 *   - 'ble': Web Bluetooth direkt im Browser (Chrome/Edge Desktop, Android).
 *            Kein Extra-Prozess. Braucht HTTPS + Nutzergeste (Klick).
 *   - 'ws' : WebSocket-Bridge `ptt_bridge.py` (BLE→WS). Funktioniert in jedem
 *            Browser, benötigt aber den laufenden Bridge-Prozess.
 *
 * BM008 GATT:
 *   Service 89a8591d-bb19-485b-9f59-58492bc33e24
 *   Char    894c8042-e841-461c-a5c9-5a73d25db08e  [notify]  (1 Byte pro Event)
 * Byte-Codes: 0x01 PTT-down · 0x00 PTT-up · 0x08 SOS · 0x03 CH · 0x04 Laut · 0x02 Leise
 */

/** Window-CustomEvent, mit dem der Funktaster Laut/Leise an die Zieltaste
 *  meldet. PanelButton hört (optional) darauf. detail: {buttonId, delta}. */
export const HW_VOLUME_EVENT = 'livepuls:hw-volume';

export type HwButtonName = 'SOS' | 'CH' | 'LAUT' | 'LEISE';
export type HwStatus = 'idle' | 'searching' | 'connected' | 'disconnected' | 'error';
export type HwSource = 'ble' | 'ws';

export type HwEvent =
  | { kind: 'ptt'; down: boolean }
  | { kind: 'button'; name: HwButtonName }
  | { kind: 'status'; state: HwStatus; detail?: string };

const BM008_SERVICE = '89a8591d-bb19-485b-9f59-58492bc33e24';
const BM008_CHAR = '894c8042-e841-461c-a5c9-5a73d25db08e';

/* --- Minimale Web-Bluetooth-Typen (DOM-Lib enthält sie nicht) --- */
interface BleChar {
  startNotifications(): Promise<void>;
  addEventListener(t: 'characteristicvaluechanged', cb: (e: { target: { value?: DataView } }) => void): void;
  removeEventListener(t: 'characteristicvaluechanged', cb: (e: { target: { value?: DataView } }) => void): void;
}
interface BleService {
  getCharacteristic(uuid: string): Promise<BleChar>;
}
interface BleServer {
  connect(): Promise<BleServer>;
  disconnect(): void;
  getPrimaryService(uuid: string): Promise<BleService>;
}
interface BleDevice {
  name?: string;
  gatt?: BleServer;
  addEventListener(t: 'gattserverdisconnected', cb: () => void): void;
  removeEventListener(t: 'gattserverdisconnected', cb: () => void): void;
}
interface BluetoothLike {
  requestDevice(opts: {
    filters?: Array<{ namePrefix?: string }>;
    optionalServices?: string[];
  }): Promise<BleDevice>;
}

function getBluetooth(): BluetoothLike | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as unknown as { bluetooth?: BluetoothLike }).bluetooth ?? null;
}

/** Welche Quellen sind in dieser Umgebung überhaupt möglich? */
export function hwSupport(): { ble: boolean; ws: boolean } {
  return {
    ble: !!getBluetooth(),
    ws: typeof WebSocket !== 'undefined',
  };
}

/* ---------------- Event-Bus + Status ---------------- */
type Listener = (e: HwEvent) => void;
const listeners = new Set<Listener>();
let status: HwStatus = 'idle';

export function onHwEvent(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
export function hwCurrentStatus(): HwStatus {
  return status;
}

function emit(e: HwEvent): void {
  if (e.kind === 'status') status = e.state;
  for (const l of listeners) {
    try {
      l(e);
    } catch (err) {
      console.warn('[hw-ptt] listener error', err);
    }
  }
}

function handleCode(code: number): void {
  switch (code) {
    case 0x01:
      emit({ kind: 'ptt', down: true });
      break;
    case 0x00:
      emit({ kind: 'ptt', down: false });
      break;
    case 0x08:
      emit({ kind: 'button', name: 'SOS' });
      break;
    case 0x03:
      emit({ kind: 'button', name: 'CH' });
      break;
    case 0x04:
      emit({ kind: 'button', name: 'LAUT' });
      break;
    case 0x02:
      emit({ kind: 'button', name: 'LEISE' });
      break;
    default:
      break;
  }
}

/* ---------------- Aktive Verbindung ---------------- */
let active: { disconnect: () => void } | null = null;

export function hwIsConnected(): boolean {
  return active !== null && status === 'connected';
}

export async function hwDisconnect(): Promise<void> {
  const a = active;
  active = null;
  if (a) {
    try {
      a.disconnect();
    } catch (err) {
      console.warn('[hw-ptt] disconnect error', err);
    }
  }
  emit({ kind: 'status', state: 'disconnected' });
}

export async function hwConnect(source: HwSource, opts?: { url?: string }): Promise<void> {
  await hwDisconnect();
  if (source === 'ble') return connectBle();
  return connectWs(opts?.url ?? 'ws://localhost:8770');
}

/* ---------------- Web Bluetooth ---------------- */
async function connectBle(): Promise<void> {
  const bt = getBluetooth();
  if (!bt) {
    emit({ kind: 'status', state: 'error', detail: 'Web Bluetooth wird hier nicht unterstützt.' });
    throw new Error('web-bluetooth-unsupported');
  }
  emit({ kind: 'status', state: 'searching' });
  let device: BleDevice;
  try {
    device = await bt.requestDevice({
      filters: [{ namePrefix: 'BM008' }],
      optionalServices: [BM008_SERVICE],
    });
  } catch (err) {
    // Nutzer hat den Geräte-Dialog abgebrochen o. Ä. → sauber zurück auf idle.
    emit({ kind: 'status', state: 'idle' });
    throw err;
  }

  const onDisconnected = (): void => {
    if (active) {
      active = null;
      emit({ kind: 'status', state: 'disconnected' });
    }
  };
  const onValue = (e: { target: { value?: DataView } }): void => {
    const dv = e.target.value;
    if (!dv || dv.byteLength < 1) return;
    handleCode(dv.getUint8(0));
  };

  try {
    device.addEventListener('gattserverdisconnected', onDisconnected);
    const server = await device.gatt!.connect();
    const svc = await server.getPrimaryService(BM008_SERVICE);
    const ch = await svc.getCharacteristic(BM008_CHAR);
    await ch.startNotifications();
    ch.addEventListener('characteristicvaluechanged', onValue);

    active = {
      disconnect: () => {
        try {
          ch.removeEventListener('characteristicvaluechanged', onValue);
          device.removeEventListener('gattserverdisconnected', onDisconnected);
          device.gatt?.disconnect();
        } catch {
          /* egal */
        }
      },
    };
    emit({ kind: 'status', state: 'connected', detail: device.name ?? 'BM008' });
  } catch (err) {
    emit({ kind: 'status', state: 'error', detail: (err as Error).message });
    throw err;
  }
}

/* ---------------- WebSocket-Bridge ---------------- */
function connectWs(url: string): Promise<void> {
  emit({ kind: 'status', state: 'searching' });
  let closedByUs = false;
  let ws: WebSocket | null = null;
  let retryTimer: number | undefined;

  const open = (): void => {
    try {
      ws = new WebSocket(url);
    } catch (err) {
      emit({ kind: 'status', state: 'error', detail: (err as Error).message });
      return;
    }
    ws.onopen = () => emit({ kind: 'status', state: 'connected', detail: url });
    ws.onmessage = (m) => {
      try {
        mapBridge(JSON.parse(String(m.data)));
      } catch {
        /* Nicht-JSON ignorieren */
      }
    };
    ws.onclose = () => {
      if (closedByUs) return;
      emit({ kind: 'status', state: 'disconnected' });
      // Auto-Reconnect, solange DIESE Verbindung die aktive ist.
      retryTimer = window.setTimeout(() => {
        if (!closedByUs) open();
      }, 1500);
    };
    ws.onerror = () => {
      try {
        ws?.close();
      } catch {
        /* egal */
      }
    };
  };

  active = {
    disconnect: () => {
      closedByUs = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      try {
        ws?.close();
      } catch {
        /* egal */
      }
    },
  };
  open();
  return Promise.resolve();
}

function mapBridge(d: unknown): void {
  if (typeof d !== 'object' || d === null) return;
  const o = d as { type?: string; state?: string; name?: string };
  if (o.type === 'ptt') {
    emit({ kind: 'ptt', down: o.state === 'down' });
  } else if (o.type === 'button' && o.name) {
    const name = o.name.toUpperCase();
    if (name === 'SOS' || name === 'CH' || name === 'LAUT' || name === 'LEISE') {
      emit({ kind: 'button', name });
    }
  }
  // {type:'status'} der Bridge ignorieren – unser Status kommt aus dem Socket.
}
